import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Compass, Radar, Route, RotateCcw } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { api } from "../lib/api";
import { clearGoal } from "../lib/goal";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export const Shell = ({ children, active }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const NAV = [
    { key: "plan", label: t("nav.plan"), icon: Compass, path: "/plan" },
    { key: "watch", label: t("nav.watch"), icon: Radar, path: "/watch" },
    { key: "journey", label: t("nav.journey"), icon: Route, path: "/journey" },
  ];

  const reset = async () => {
    try {
      await api.resetDemo();
      clearGoal();
      toast.success(t("common.resetToastTitle"), { description: t("common.resetToastDesc") });
      navigate("/");
    } catch {
      toast.error(t("common.resetToastErr"));
    }
  };

  const current = active || NAV.find((n) => location.pathname.startsWith(n.path))?.key;

  return (
    <div className="min-h-screen" style={{ background: "var(--paper)" }}>
      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(245,246,248,0.82)", borderBottom: "1px solid var(--line)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-2">
          <button onClick={() => navigate("/")} data-testid="shell-home-button" className="active:scale-95 transition-transform">
            <Logo size={28} />
          </button>
          <div className="flex items-center gap-2">
            <span className="dv-pill text-[11px] px-2.5 py-1 hidden sm:inline-flex" style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--line)" }} data-testid="demo-data-chip" title={t("common.demoData")}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--line-strong)" }} />
              {t("common.demoData")}
            </span>
            <LanguageSwitcher />
            <button onClick={reset} data-testid="demo-reset-button" className="dv-btn dv-btn-ghost text-xs px-3 py-1.5" title={t("common.reset")}>
              <RotateCcw size={13} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-28 pt-5">{children}</main>

      <nav className="fixed bottom-0 inset-x-0 z-40" data-testid="bottom-nav">
        <div className="max-w-5xl mx-auto px-4 pb-[max(10px,env(safe-area-inset-bottom))] pt-2">
          <div className="dv-card flex items-stretch justify-around px-2 py-1.5" style={{ borderRadius: "1.6rem", boxShadow: "0 12px 40px rgba(15,23,42,0.16)" }}>
            {NAV.map((n) => {
              const Icon = n.icon;
              const on = current === n.key;
              return (
                <button key={n.key} onClick={() => navigate(n.path)} data-testid={`nav-${n.key}`}
                  className="flex-1 flex flex-col items-center gap-1 py-2 rounded-2xl transition-colors"
                  style={{ color: on ? "var(--ink)" : "var(--muted)", background: on ? "var(--brand-soft)" : "transparent" }}>
                  <Icon size={20} strokeWidth={on ? 2.4 : 1.9} color={on ? "var(--brand)" : "var(--muted)"} />
                  <span className="text-[11px] font-semibold font-display">{n.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
};
